import { useEffect, useState, useRef } from 'react';
import { EntityType, RelatedEntity } from '@/types/link';
import { useLinkStore } from '@/stores/linkStore';
import { useRisks, useTasks, useEvidence } from '@/hooks/queries';
import { useAuth } from '@/hooks/useAuth';
import { useSupplierStore } from '@/stores/supplierStore';
import { useSiteStore } from '@/stores/siteStore';
import { useZoneStore } from '@/stores/zoneStore';
import { useSystemStore } from '@/stores/systemStore';
import { useEndpointStore } from '@/stores/endpointStore';
import { useCloudResourceStore } from '@/stores/cloudResourceStore';
import { useInternetConnectionStore } from '@/stores/internetConnectionStore';
import { useDMZConfigStore } from '@/stores/dmzConfigStore';

import { supabase } from '@/integrations/supabase/client';
import { type Evidence } from '@/hooks/queries/useEvidence';
import { type Task } from '@/hooks/queries/useTasks';
import { type Risk } from '@/hooks/queries/useRisks';
import type { Supplier } from '@/types/supplier';
import type { Site } from '@/types/site';
import type { Zone } from '@/types/zone';
import type { System } from '@/types/system';
import type { Endpoint } from '@/types/endpoint';
import type { CloudResource } from '@/types/cloudResource';
import type { InternetConnection } from '@/types/internetConnection';
import type { DMZConfig } from '@/types/dmzConfig';

import type { Database } from '@/integrations/supabase/types';

type ControlSummary = Pick<Database['public']['Tables']['controls']['Row'], 'id' | 'title' | 'control_id' | 'status'>;
type IncidentSummary = Pick<Database['public']['Tables']['incidents']['Row'], 'id' | 'title' | 'priority' | 'status'>;

interface RelatedEntities {
  assets: RelatedEntity[];
  sites: RelatedEntity[];
  zones: RelatedEntity[];
  systems: RelatedEntity[];
  endpoints: RelatedEntity[];
  cloudResources: RelatedEntity[];
  internetConnections: RelatedEntity[];
  dmzConfigs: RelatedEntity[];
  risks: RelatedEntity[];
  controls: RelatedEntity[];
  suppliers: RelatedEntity[];
  incidents: RelatedEntity[];
  tasks: RelatedEntity[];
  evidence: RelatedEntity[];
  loading: boolean;
}

export function useRelatedEntities(entityType: EntityType, entityId: string) {
  const [relatedEntities, setRelatedEntities] = useState<RelatedEntities>({
    assets: [],
    sites: [],
    zones: [],
    systems: [],
    endpoints: [],
    cloudResources: [],
    internetConnections: [],
    dmzConfigs: [],
    risks: [],
    controls: [],
    suppliers: [],
    incidents: [],
    tasks: [],
    evidence: [],
    loading: true,
  });
  const { organization } = useAuth();
  const { getLinkedEntities, loadLinks } = useLinkStore();
  const { risks } = useRisks(organization?.id);
  const { tasks } = useTasks(organization?.id);
  const { evidence } = useEvidence(organization?.id);
  const { suppliers } = useSupplierStore();
  const { sites } = useSiteStore();
  const { zones } = useZoneStore();
  const { systems } = useSystemStore();
  const { endpoints } = useEndpointStore();
  const { cloudResources } = useCloudResourceStore();
  const { connections: internetConnections } = useInternetConnectionStore();
  const { configs: dmzConfigs } = useDMZConfigStore();

  const initialLoadDone = useRef(false);

  useEffect(() => {
    if (!organization?.id || !entityId) {
      setRelatedEntities(prev => ({ ...prev, loading: false }));
      return;
    }

    const fetchRelatedEntities = async () => {
      // Only show loading on first load to prevent flickering
      if (!initialLoadDone.current) {
        setRelatedEntities(prev => ({ ...prev, loading: true }));
      }

      // Load links for this entity
      await loadLinks(entityType, entityId);

      // Get linked entity IDs
      const links = getLinkedEntities(entityType, entityId);

      const riskItems = risks as Risk[];
      const taskItems = tasks as Task[];
      const evidenceItems = evidence as Evidence[];
      const supplierItems = suppliers as Supplier[];
      const siteItems = sites as Site[];
      const zoneItems = zones as Zone[];
      const systemItems = systems as System[];
      const endpointItems = endpoints as Endpoint[];
      const cloudResourceItems = cloudResources as CloudResource[];
      const internetConnectionItems = internetConnections as InternetConnection[];
      const dmzConfigItems = dmzConfigs as DMZConfig[];
      

      const controlIds = links
        .filter(link => link.source_type === 'control' || link.target_type === 'control')
        .map(link => (link.source_type === 'control' ? link.source_id : link.target_id));

      const incidentIds = links
        .filter(link => link.source_type === 'incident' || link.target_type === 'incident')
        .map(link => (link.source_type === 'incident' ? link.source_id : link.target_id));

      // Fetch controls and incidents from Supabase
      let controlsData: ControlSummary[] = [];
      let incidentsData: IncidentSummary[] = [];

      if (controlIds.length) {
        const { data } = await supabase
          .from('controls')
          .select('id, title, control_id, status')
          .in('id', controlIds);

        controlsData = data ?? [];
      }

      if (incidentIds.length) {
        const { data } = await supabase
          .from('incidents')
          .select('id, title, priority, status')
          .in('id', incidentIds);

        incidentsData = data ?? [];
      }

      // Map entity IDs to actual entities
      const related: Omit<RelatedEntities, 'loading'> = {
        assets: [], // Legacy - kept for backwards compatibility

        sites: links
          .filter(l => l.source_type === 'site' || l.target_type === 'site')
          .map(l => {
            const siteId = l.source_type === 'site' ? l.source_id : l.target_id;
            const site = siteItems.find(s => s.id === siteId);
            if (!site) return null;

            const entity: RelatedEntity = {
              id: site.id,
              type: 'site',
              name: site.name,
            };

            return entity;
          })
          .filter((entity): entity is RelatedEntity => entity !== null),

        zones: links
          .filter(l => l.source_type === 'zone' || l.target_type === 'zone')
          .map(l => {
            const zoneId = l.source_type === 'zone' ? l.source_id : l.target_id;
            const zone = zoneItems.find(z => z.id === zoneId);
            if (!zone) return null;

            const entity: RelatedEntity = {
              id: zone.id,
              type: 'zone',
              name: zone.name,
            };

            return entity;
          })
          .filter((entity): entity is RelatedEntity => entity !== null),

        systems: links
          .filter(l => l.source_type === 'system' || l.target_type === 'system')
          .map(l => {
            const systemId = l.source_type === 'system' ? l.source_id : l.target_id;
            const system = systemItems.find(s => s.id === systemId);
            if (!system) return null;

            const entity: RelatedEntity = {
              id: system.id,
              type: 'system',
              name: system.name,
            };

            return entity;
          })
          .filter((entity): entity is RelatedEntity => entity !== null),

        endpoints: links
          .filter(l => l.source_type === 'endpoint' || l.target_type === 'endpoint')
          .map(l => {
            const endpointId = l.source_type === 'endpoint' ? l.source_id : l.target_id;
            const endpoint = endpointItems.find(e => e.id === endpointId);
            if (!endpoint) return null;

            const entity: RelatedEntity = {
              id: endpoint.id,
              type: 'endpoint',
              name: endpoint.name,
            };

            return entity;
          })
          .filter((entity): entity is RelatedEntity => entity !== null),

        cloudResources: links
          .filter(l => l.source_type === 'cloud_resource' || l.target_type === 'cloud_resource')
          .map(l => {
            const crId = l.source_type === 'cloud_resource' ? l.source_id : l.target_id;
            const cr = cloudResourceItems.find(c => c.id === crId);
            if (!cr) return null;

            const entity: RelatedEntity = {
              id: cr.id,
              type: 'cloud_resource',
              name: cr.name,
            };

            return entity;
          })
          .filter((entity): entity is RelatedEntity => entity !== null),

        internetConnections: links
          .filter(l => l.source_type === 'internet_connection' || l.target_type === 'internet_connection')
          .map(l => {
            const icId = l.source_type === 'internet_connection' ? l.source_id : l.target_id;
            const ic = internetConnectionItems.find(i => i.id === icId);
            if (!ic) return null;

            const entity: RelatedEntity = {
              id: ic.id,
              type: 'internet_connection',
              name: ic.label,
            };

            return entity;
          })
          .filter((entity): entity is RelatedEntity => entity !== null),

        dmzConfigs: links
          .filter(l => l.source_type === 'dmz_config' || l.target_type === 'dmz_config')
          .map(l => {
            const dmzId = l.source_type === 'dmz_config' ? l.source_id : l.target_id;
            const dmz = dmzConfigItems.find(d => d.id === dmzId);
            if (!dmz) return null;

            const entity: RelatedEntity = {
              id: dmz.id,
              type: 'dmz_config',
              name: dmz.label,
            };

            return entity;
          })
          .filter((entity): entity is RelatedEntity => entity !== null),

        risks: links
          .filter(l => l.source_type === 'risk' || l.target_type === 'risk')
          .map(l => {
            const riskId = l.source_type === 'risk' ? l.source_id : l.target_id;
            const risk = riskItems.find(r => r.id === riskId);
            if (!risk) return null;

            const metadata: Record<string, unknown> = { severity: risk.status };
            const entity: RelatedEntity = {
              id: risk.id,
              type: 'risk',
              name: risk.title,
              metadata,
            };

            return entity;
          })
          .filter((entity): entity is RelatedEntity => entity !== null),

        controls: controlsData.map(control => {
          const metadata: Record<string, unknown> = {
            control_id: control.control_id,
            status: control.status,
          };

          const entity: RelatedEntity = {
            id: control.id,
            type: 'control',
            name: control.title,
            metadata,
          };

          return entity;
        }),

        suppliers: links
          .filter(l => l.source_type === 'supplier' || l.target_type === 'supplier')
          .map(l => {
            const supplierId = l.source_type === 'supplier' ? l.source_id : l.target_id;
            const supplier = supplierItems.find(s => s.id === supplierId);
            if (!supplier) return null;

            const entity: RelatedEntity = {
              id: supplier.id,
              type: 'supplier',
              name: supplier.name,
            };

            return entity;
          })
          .filter((entity): entity is RelatedEntity => entity !== null),

        incidents: incidentsData.map(incident => {
          const metadata: Record<string, unknown> = {
            priority: incident.priority,
            status: incident.status,
          };

          const entity: RelatedEntity = {
            id: incident.id,
            type: 'incident',
            name: incident.title,
            metadata,
          };

          return entity;
        }),

        tasks: links
          .filter(l => l.source_type === 'task' || l.target_type === 'task')
          .map(l => {
            const taskId = l.source_type === 'task' ? l.source_id : l.target_id;
            const task = taskItems.find(t => t.id === taskId);
            if (!task) return null;

            const metadata: Record<string, unknown> = { status: task.status };
            const entity: RelatedEntity = {
              id: task.id,
              type: 'task',
              name: task.title,
              metadata,
            };

            return entity;
          })
          .filter((entity): entity is RelatedEntity => entity !== null),

        evidence: links
          .filter(l => l.source_type === 'evidence' || l.target_type === 'evidence')
          .map(l => {
            const evidenceId = l.source_type === 'evidence' ? l.source_id : l.target_id;
            const ev = evidenceItems.find(item => item.id === evidenceId);
            if (!ev) return null;

            const metadata: Record<string, unknown> = { type: ev.type };
            const entity: RelatedEntity = {
              id: ev.id,
              type: 'evidence',
              name: ev.name,
              metadata,
            };

            return entity;
          })
          .filter((entity): entity is RelatedEntity => entity !== null),
      };

      setRelatedEntities({ ...related, loading: false });
      initialLoadDone.current = true;
    };

    if (entityId) {
      fetchRelatedEntities();
    }
  }, [entityId, entityType, organization?.id]); // Removed 'links' to prevent re-render loop

  return relatedEntities;
}
